import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import MessageBubble from './components/MessageBubble';
import ShareButton from './components/ShareButton';
import ExportMenu from './components/ExportMenu';
import ShareView from './pages/ShareView';
import { Message, Mode } from './types';
import { generateShareImage, shareBlobOrDownload } from './utils/shareImage';
import { useRoute } from './routes';

const STORAGE_KEY = 'kopeika_bot_chat';
const MODE_KEY = 'kopeika_bot_mode';
const REACTIONS_KEY = 'kopeika_bot_reactions';
const SHARE_NUDGE_TEXT = '💡 Това става за шерване';
const QUICK_START = [
  'Дай ми опорка',
  'Разкажи ми за еврото',
  'Унижи ме с патриотизъм',
];
const RANDOM_GOLD = 'Дай ми бруталната тирада за деня!';

type ReactionStore = Record<string, Record<string, number>>;

function getMessageKey(message: Message, index: number) {
  return `${message.role}-${index}-${message.content.slice(0, 24)}`;
}

function App() {
  const route = useRoute();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState<Mode>('snappy');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [shareLinkError, setShareLinkError] = useState<string | null>(null);
  const [shareToast, setShareToast] = useState(false);
  const [highlightShare, setHighlightShare] = useState(false);
  const [reactions, setReactions] = useState<ReactionStore>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const botCountRef = useRef(0);
  const isMobile = useMemo(
    () => typeof window !== 'undefined' && window.innerWidth < 900,
    []
  );

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setMessages(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved messages:', e);
      }
    }

    const savedMode = localStorage.getItem(MODE_KEY);
    if (savedMode === 'soft' || savedMode === 'snappy') {
      setMode(savedMode);
    }

    const savedReactions = localStorage.getItem(REACTIONS_KEY);
    if (savedReactions) {
      try {
        setReactions(JSON.parse(savedReactions));
      } catch (e) {
        console.error('Failed to parse reactions:', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem(REACTIONS_KEY, JSON.stringify(reactions));
  }, [reactions]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const botReplies = messages.filter((m) => m.role === 'assistant').length;
    if (botReplies > 0 && botReplies % 3 === 0 && botReplies !== botCountRef.current) {
      setShareToast(true);
      setTimeout(() => setShareToast(false), 2800);
    }
    botCountRef.current = botReplies;

    const last = messages[messages.length - 1];
    if (
      last?.role === 'assistant' &&
      (last.content.length > 220 || /!{3,}/.test(last.content))
    ) {
      setHighlightShare(true);
      setTimeout(() => setHighlightShare(false), 2200);
    }
  }, [messages]);

  const sendMessage = async (overrideInput?: string) => {
    const content = (overrideInput ?? input).trim();
    if (!content || loading) return;

    const userMessage: Message = {
      role: 'user',
      content,
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: newMessages,
          mode,
        }),
      });

      const data = (await response.json()) as {
        error?: string;
        reply?: string;
      };

      if (!response.ok) {
        setError(data.error || 'Нещо се скапа.');
        setLoading(false);
        return;
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: data.reply || '',
      };

      setMessages([...newMessages, assistantMessage]);
    } catch (err) {
      setError('Мрежата се спъна. Пробвай пак.');
      console.error('Chat error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem(STORAGE_KEY);
    setError(null);
  };

  const resetPersona = () => {
    clearChat();
    setMode('snappy');
    localStorage.removeItem(MODE_KEY);
  };

  const toggleMode = () => {
    setMode(mode === 'soft' ? 'snappy' : 'soft');
  };

  const shareImage = async (subset?: Message[]) => {
    const base = subset && subset.length ? subset : messages.slice(-4);
    if (!base.length) return;
    const blob = await generateShareImage(base, {
      watermark: 'botkop.pages.dev',
      label: 'Сатира / Пародия',
      botName: 'BOTKOP',
      botAvatar: '/kopeikabot.webp',
    });
    await shareBlobOrDownload(
      blob,
      'botkop-share.png',
      isMobile,
      'botkop.pages.dev'
    );
  };

  const shareForMessage = async (index: number) => {
    const start = Math.max(0, index - 3);
    await shareImage(messages.slice(start, index + 1));
  };

  const shareLinkSnapshot = async () => {
    if (!messages.length) return;
    setShareLink(null);
    setShareLinkError(null);
    try {
      const payload = { messages: messages.slice(-6) };
      const res = await fetch('/api/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as { id?: string; error?: string };
      if (!res.ok || !data.id) {
        setShareLinkError(data.error || 'Не успях да направя линк.');
        return;
      }
      const url = `${window.location.origin}/s/${data.id}`;
      setShareLink(url);
      try {
        await navigator.clipboard.writeText(url);
      } catch (err) {
        console.warn('Clipboard unavailable', err);
      }
    } catch (err) {
      console.error(err);
      setShareLinkError('Линкът се спъна. Опитай пак.');
    }
  };

  const exportAsText = async () => {
    if (!messages.length) return;
    const text = messages
      .slice(-10)
      .map((m) => `${m.role === 'assistant' ? 'BOTKOP' : 'ТИ'}: ${m.content}`)
      .join('\n');
    await navigator.clipboard.writeText(text);
  };

  const handleReact = (index: number, emoji: string) => {
    const key = getMessageKey(messages[index], index);
    setReactions((prev) => {
      const current = prev[key] || {};
      return {
        ...prev,
        [key]: {
          ...current,
          [emoji]: (current[emoji] || 0) + 1,
        },
      };
    });
  };

  const startQuickPrompt = (prompt: string) => {
    sendMessage(prompt);
  };

  if (route.name === 'share') {
    return <ShareView id={route.id} />;
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <a href="/" className="logo-stack" onClick={(e) => { e.preventDefault(); clearChat(); }}>
            <picture>
              <source srcSet="/kopeikabot-h70.avif" type="image/avif" media="(min-width: 901px)" />
              <source srcSet="/kopeikabot-h70.webp" type="image/webp" media="(min-width: 901px)" />
              <source srcSet="/kopeikabot-h48.avif" type="image/avif" media="(max-width: 900px)" />
              <source srcSet="/kopeikabot-h48.webp" type="image/webp" media="(max-width: 900px)" />
              <img
                src="/kopeikabot-h70.webp"
                className="logo"
                alt="Botkop"
                width="70"
                height="70"
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
            </picture>
            <div className="logo-bubble" aria-hidden="true">Хей жълтопаветнико!</div>
            <div className="tagline">Сатиричен бот</div>
          </a>
          <div className="controls">
            <button onClick={toggleMode} className="mode-toggle">
              {mode === 'soft' ? '😢' : '🔥'}
            </button>
            <button className="ghost" onClick={() => startQuickPrompt(RANDOM_GOLD)}>
              Рандом злато
            </button>
            <a
              href="https://ko-fi.com/insectninja"
              target="_blank"
              rel="noopener noreferrer"
              className="kofi-btn"
            >
              💶 5€ ШАМАР
            </a>
            {messages.length > 0 && (
              <>
                <button onClick={clearChat} className="clear-btn hide-mobile">
                  Изчисти чат
                </button>
                <button onClick={resetPersona} className="reset-btn hide-mobile">
                  Рестартирай персонажа
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="chat-container">
        {shareToast && <div className="share-toast">{SHARE_NUDGE_TEXT}</div>}
        {shareLink && (
          <div className="share-link">
            <span>Линк готов:</span>
            <a href={shareLink}>{shareLink}</a>
          </div>
        )}
        {shareLinkError && <div className="error-message">{shareLinkError}</div>}

        <div className="messages">
          {messages.length === 0 && !loading && (
            <div className="viral-empty">
              <div className="viral-buttons">
                {QUICK_START.map((cta) => (
                  <button key={cta} onClick={() => startQuickPrompt(cta)}>
                    {cta}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <MessageBubble
              key={idx}
              message={msg}
              index={idx}
              showShare={msg.role === 'assistant'}
              onShare={() => shareForMessage(idx)}
              highlightShare={highlightShare && idx === messages.length - 1}
              reactions={reactions[getMessageKey(msg, idx)]}
              onReact={(emoji) => handleReact(idx, emoji)}
            />
          ))}

          {loading && (
            <div className="message assistant">
              <div className="message-content loading">Бърка туршия...</div>
            </div>
          )}
          {error && <div className="error-message">⚠ {error}</div>}
          <div ref={messagesEndRef} />
        </div>

        <div className="bar-row hide-mobile">
          <ShareButton
            label="Шерни снимка"
            onShare={() => shareImage(messages.slice(-4))}
            highlight={highlightShare}
          />
          <button className="ghost" onClick={shareLinkSnapshot}>
            Шерни линк
          </button>
          <ExportMenu
            onExportImage={() => shareImage(messages.slice(-4))}
            onExportText={exportAsText}
          />
        </div>

        <div className="input-area">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Казвай жълтопаветен планктон!"
            disabled={loading}
            rows={3}
          />
          <button onClick={() => sendMessage()} disabled={loading || !input.trim()}>
            Прати
          </button>
        </div>

        <div className="input-hint">Евро, рубла — все тая. Простотията е безплатна. Само токът не.</div>
      </main>
    </div>
  );
}

export default App;
